/**
 * Inline AI Suggestion Component
 *
 * Displays AI suggestions inline in the editor with accept/reject controls.
 */
import React, { useState, useEffect, useCallback } from 'react';

export interface AISuggestion {
  id: string;
  originalCode: string;
  suggestedCode: string;
  explanation?: string;
  action: 'refactor' | 'fix' | 'optimize' | 'document';
  confidence?: number;
  position: {
    lineNumber: number;
    column: number;
  };
}

interface InlineAISuggestionProps {
  suggestion: AISuggestion;
  onAccept: (suggestion: AISuggestion) => void;
  onReject: (suggestion: AISuggestion) => void;
  onModify?: (suggestion: AISuggestion, modifiedCode: string) => void;
}

/**
 * Simple diff view component.
 */
function DiffView({
  original,
  suggested,
}: {
  original: string;
  suggested: string;
}) {
  const originalLines = original.split('\n');
  const suggestedLines = suggested.split('\n');

  return (
    <div className="font-mono text-xs overflow-x-auto">
      <div className="mb-2">
        <div className="text-vibe-error text-xs font-medium mb-1">- Original</div>
        <pre className="bg-red-500/10 text-red-300 p-2 rounded border border-red-500/20 overflow-x-auto">
          {originalLines.map((line, i) => (
            <div key={`old-${i}`} className="opacity-70">
              {line || ' '}
            </div>
          ))}
        </pre>
      </div>
      <div>
        <div className="text-vibe-success text-xs font-medium mb-1">+ Suggested</div>
        <pre className="bg-green-500/10 text-green-300 p-2 rounded border border-green-500/20 overflow-x-auto">
          {suggestedLines.map((line, i) => (
            <div key={`new-${i}`}>
              {line || ' '}
            </div>
          ))}
        </pre>
      </div>
    </div>
  );
}

/**
 * Action badge component.
 */
function ActionBadge({ action }: { action: AISuggestion['action'] }) {
  const config = {
    refactor: { label: 'Refactor', color: 'bg-purple-500/20 text-purple-300' },
    fix: { label: 'Fix', color: 'bg-red-500/20 text-red-300' },
    optimize: { label: 'Optimize', color: 'bg-yellow-500/20 text-yellow-300' },
    document: { label: 'Document', color: 'bg-blue-500/20 text-blue-300' },
  };

  const { label, color } = config[action];

  return (
    <span className={`px-2 py-0.5 text-xs rounded-full ${color}`}>{label}</span>
  );
}

export function InlineAISuggestion({
  suggestion,
  onAccept,
  onReject,
  onModify,
}: InlineAISuggestionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedCode, setEditedCode] = useState(suggestion.suggestedCode);

  // Reset edited code when suggestion changes
  useEffect(() => {
    setEditedCode(suggestion.suggestedCode);
    setIsEditing(false);
  }, [suggestion]);

  const handleAccept = useCallback(() => {
    if (isEditing) {
      onModify?.(suggestion, editedCode);
    } else {
      onAccept(suggestion);
    }
  }, [suggestion, isEditing, editedCode, onAccept, onModify]);

  const handleReject = useCallback(() => {
    onReject(suggestion);
  }, [suggestion, onReject]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleAccept();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        if (isEditing) {
          setIsEditing(false);
          setEditedCode(suggestion.suggestedCode);
        } else {
          handleReject();
        }
      }
    },
    [handleAccept, handleReject, isEditing, suggestion.suggestedCode]
  );

  return (
    <div
      className="bg-vibe-bg-secondary border border-vibe-accent/30 rounded-lg shadow-lg overflow-hidden"
      onKeyDown={handleKeyDown}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-vibe-accent/10 border-b border-vibe-accent/20">
        <div className="flex items-center gap-2">
          <span className="text-vibe-accent">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                clipRule="evenodd"
              />
            </svg>
          </span>
          <span className="text-sm font-medium text-vibe-text">AI Suggestion</span>
          <ActionBadge action={suggestion.action} />
          {suggestion.confidence !== undefined && (
            <span className="text-xs text-vibe-text-tertiary">
              {Math.round(suggestion.confidence * 100)}% confidence
            </span>
          )}
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-vibe-text-tertiary hover:text-vibe-text transition-colors"
        >
          <svg
            className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Explanation */}
        {suggestion.explanation && (
          <p className="text-sm text-vibe-text-secondary mb-3">
            {suggestion.explanation}
          </p>
        )}

        {/* Diff or edit view */}
        {isEditing ? (
          <div className="mb-3">
            <div className="text-vibe-text text-xs font-medium mb-1">Edit suggestion:</div>
            <textarea
              value={editedCode}
              onChange={(e) => setEditedCode(e.target.value)}
              className="w-full h-40 bg-vibe-bg border border-vibe-border rounded p-2 font-mono text-xs text-vibe-text resize-y focus:outline-none focus:border-vibe-accent"
              autoFocus
            />
          </div>
        ) : isExpanded ? (
          <DiffView original={suggestion.originalCode} suggested={suggestion.suggestedCode} />
        ) : (
          <div className="font-mono text-xs mb-3">
            <pre className="bg-vibe-bg p-2 rounded border border-vibe-border text-vibe-text overflow-x-auto max-h-24">
              {suggestion.suggestedCode.split('\n').slice(0, 5).join('\n')}
              {suggestion.suggestedCode.split('\n').length > 5 && (
                <span className="text-vibe-text-tertiary">
                  {'\n'}... ({suggestion.suggestedCode.split('\n').length - 5} more lines)
                </span>
              )}
            </pre>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleAccept}
            className="flex-1 px-3 py-1.5 bg-vibe-success/20 text-vibe-success rounded hover:bg-vibe-success/30 transition-colors text-sm font-medium flex items-center justify-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            Accept
            <kbd className="ml-1 text-xs opacity-60">⌘↵</kbd>
          </button>
          <button
            onClick={handleReject}
            className="flex-1 px-3 py-1.5 bg-vibe-error/20 text-vibe-error rounded hover:bg-vibe-error/30 transition-colors text-sm font-medium flex items-center justify-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            Reject
            <kbd className="ml-1 text-xs opacity-60">Esc</kbd>
          </button>
          {onModify && !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-3 py-1.5 bg-vibe-bg-tertiary text-vibe-text-secondary rounded hover:bg-vibe-border transition-colors text-sm"
            >
              Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * AI Suggestion Manager Component
 *
 * Manages multiple inline suggestions with positioning.
 */
interface AISuggestionManagerProps {
  suggestions: AISuggestion[];
  onAccept: (suggestion: AISuggestion) => void;
  onReject: (suggestion: AISuggestion) => void;
  onModify?: (suggestion: AISuggestion, modifiedCode: string) => void;
  editorElement?: HTMLElement;
}

export function AISuggestionManager({
  suggestions,
  onAccept,
  onReject,
  onModify,
}: AISuggestionManagerProps) {
  if (suggestions.length === 0) return null;

  // Show latest suggestion
  const currentSuggestion = suggestions[suggestions.length - 1];

  return (
    <div className="fixed bottom-4 right-4 w-96 z-50">
      <InlineAISuggestion
        suggestion={currentSuggestion}
        onAccept={onAccept}
        onReject={onReject}
        onModify={onModify}
      />
      {suggestions.length > 1 && (
        <div className="text-center text-xs text-vibe-text-tertiary mt-2">
          {suggestions.length} suggestions pending
        </div>
      )}
    </div>
  );
}

export default InlineAISuggestion;
